import { PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { capitalizeString } from 'utils/stringUtils';

export const buildReviewSchema = (pageContent) => {
	if (!pageContent || !pageContent.sections) return [];

	const reviewsSection = pageContent.sections.find(
		(section) => section?.title === 'Reviews Section'
	);

	if (!reviewsSection || !reviewsSection.modules?.[0]?.props?.reviews) {
		return [];
	}

	return reviewsSection.modules[0].props.reviews.map((review) => ({
		'@type': 'Review',
		author: {
			'@type': 'Person',
			name: review.reviewerName,
		},
		datePublished: review.reviewDate,
		reviewRating: {
			'@type': 'Rating',
			ratingValue: review.reviewRating,
			bestRating: '5',
			worstRating: '1',
		},
		reviewBody: review.reviewText,
	}));
};

/**
 * Helper function to build page schema
 * Extracted and optimized to avoid recreation on every render
 */
export const buildPageSchema = (keyword, pageContent, reviewSchema, description) => {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		name: `Find ${pageContent.type.replace(/-/g, ' ')} Storage Near You`,
		url: `https://www.sparefoot.com/${keyword}-storage.html`,
		description: description || pageContent.description,
		inLanguage: 'en-US',
		isPartOf: {
			'@type': 'WebSite',
			'@id': 'https://www.sparefoot.com/#website',
		},
		about: {
			'@type': 'Service',
			serviceType: `${pageContent.type.replace(/-/g, ' ')} Storage`,
			provider: {
				'@type': 'Organization',
				name: 'SpareFoot',
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: '4.3',
					reviewCount: '49225',
					bestRating: '5',
					worstRating: '1',
				},
			},
		},
		primaryImageOfPage: {
			'@type': 'ImageObject',
			url: `https://www.sparefoot.com/static/images/psp/hero/${keyword}-hero.jpg`,
			width: 1200,
			height: 630,
		},
		breadcrumb: {
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://www.sparefoot.com/',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: `${pageContent.type} Storage`,
					item: `https://www.sparefoot.com/${keyword}-storage.html`,
				},
			],
		},
		review: reviewSchema,
	};
};

export const PSP_PAGE_BY_KEY = {
	rv: PAGE_SUB_TYPES.RV_LANDING,
	car: PAGE_SUB_TYPES.CAR_LANDING,
	boat: PAGE_SUB_TYPES.BOAT_LANDING,
	'climate-controlled': PAGE_SUB_TYPES.CLIMATE_CONTROLLED_LANDING,
	portable: PAGE_SUB_TYPES.PORTABLE_LANDING,
	motorcycle: PAGE_SUB_TYPES.MOTORCYCLE_LANDING,
	business: PAGE_SUB_TYPES.BUSINESS_LANDING,
	wine: PAGE_SUB_TYPES.WINE_LANDING,
	atv: PAGE_SUB_TYPES.ATV_LANDING,
	bicycle: PAGE_SUB_TYPES.BICYCLE_LANDING,
	'dirt-bike': PAGE_SUB_TYPES.DIRT_BIKE_LANDING,
	'golf-cart': PAGE_SUB_TYPES.GOLF_CART_LANDING,
	college: PAGE_SUB_TYPES.COLLEGE_LANDING,
	truck: PAGE_SUB_TYPES.TRUCK_LANDING,
	'jet-ski': PAGE_SUB_TYPES.JET_SKI_LANDING,
	kayak: PAGE_SUB_TYPES.KAYAK_LANDING,
	military: PAGE_SUB_TYPES.MILITARY_LANDING,
	'drive-up': PAGE_SUB_TYPES.DRIVE_UP_LANDING,
	'24-hour': PAGE_SUB_TYPES.TWENTY_FOUR_HOUR_LANDING,
	vehicle: PAGE_SUB_TYPES.VEHICLE_LANDING,
	'storage-with-electricity': PAGE_SUB_TYPES.STORAGE_WITH_ELECTRICITY_LANDING,
	'storage-101': PAGE_SUB_TYPES.STORAGE_101_LANDING,
	'storage-lockers': PAGE_SUB_TYPES.LOCKER_LANDING,
	// SGPs
	'5x5': PAGE_SUB_TYPES.FIVE_X_FIVE_STORAGE_UNIT,
	'5x10': PAGE_SUB_TYPES.FIVE_X_TEN_STORAGE_UNIT,
	'5x15': PAGE_SUB_TYPES.FIVE_X_FIFTEEN_STORAGE_UNIT,
	'10x10': PAGE_SUB_TYPES.TEN_X_TEN_STORAGE_UNIT,
	'10x15': PAGE_SUB_TYPES.TEN_X_FIFTEEN_STORAGE_UNIT,
	'10x20': PAGE_SUB_TYPES.TEN_X_TWENTY_STORAGE_UNIT,
	'10x25': PAGE_SUB_TYPES.TEN_X_TWENTY_FIVE_STORAGE_UNIT,
	'10x30': PAGE_SUB_TYPES.TEN_X_THIRTY_STORAGE_UNIT,
	Mini: PAGE_SUB_TYPES.MINI_STORAGE,
};

const buildSGPSlug = (keyword) =>
	['storage-lockers', 'mini-storage', 'mini storage'].includes(keyword)
		? keyword.replace(' ', '-')
		: `${keyword}-storage-unit`;

export const buildSGPWebPageSchema = (keyword, pageContent) => {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		url: `https://www.sparefoot.com/storage-unit-size-guide/${buildSGPSlug(keyword)}.html`,
		name: `${capitalizeString(keyword)} Storage Unit Size Guide | What Fits in a ${capitalizeString(keyword)} Unit?`,
		description: pageContent.description,
		inLanguage: 'en-US',
		isPartOf: {
			'@type': 'WebSite',
			name: 'SpareFoot',
			url: 'https://www.sparefoot.com',
		},
	};
};

export const buildSGPWebsiteSchema = () => {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'SpareFoot',
		url: 'https://www.sparefoot.com/',
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate:
					'https://www.sparefoot.com/search?location={search_term_string}',
				inLanguage: 'en-US',
				actionPlatform: [
					'http://schema.org/DesktopWebPlatform',
					'http://schema.org/MobileWebPlatform',
				],
			},
			'query-input': {
				'@type': 'PropertyValueSpecification',
				valueRequired: 'http://schema.org/True',
				valueName: 'search_term_string',
			},
		},
	};
};

/**
 * WebPage schema for the main Size Guide page (storage-unit-size-guide.html).
 * Used by SizeGuidePage to match PSPPage's schema pattern.
 */
export const buildSizeGuideMainPageWebPageSchema = () => {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebPage',
		url: 'https://www.sparefoot.com/storage-unit-size-guide.html',
		name: 'Storage Unit Size Guide',
		description:
			"Use SpareFoot’s storage unit size guide to compare sizes from 5x5 to 10x30. Compare dimensions, see what fits, and make a smart storage choice.",
		inLanguage: 'en-US',
		isPartOf: {
			'@type': 'WebSite',
			name: 'SpareFoot',
			url: 'https://www.sparefoot.com',
		},
	};
};

export const buildSGPMainSizeGuidePageSchema = () => {
	return {
		'@context': 'https://schema.org',
		'@type': 'Guide',
		url: 'https://www.sparefoot.com/storage-unit-size-guide.html',
		name: 'Storage Unit Size Guide',
		description:
			"SpareFoot's storage unit size guide helps you compare unit sizes from 5x5 to 10x30. Learn what fits in each space and find the right size for your needs.",
		inLanguage: 'en-US',
		about: {
			'@type': 'Thing',
			name: 'Self Storage Units',
		},
		isPartOf: {
			'@type': 'WebPage',
			url: 'https://www.sparefoot.com/storage-unit-size-guide.html',
		},
	};
};

export const buildSGPIndividualPagesGuideSchema = (keyword, pageContent) => {
	return {
		'@context': 'https://schema.org',
		'@type': 'Guide',
		name: `${capitalizeString(pageContent?.type)} Storage Unit Size Guide`,
		url: `https://www.sparefoot.com/storage-unit-size-guide/${buildSGPSlug(keyword)}.html`,
		description: `Learn what fits in a ${pageContent?.type} storage unit. See dimensions, storage tips, and real-life examples to help you decide on the right size for your needs.`,
		inLanguage: 'en-US',
		about: {
			'@type': 'Thing',
			name: 'Storage Units',
			url: 'https://www.sparefoot.com/storage-unit-size-guide.html',
		},
	};
};
